<template>
  <app-layout header-type="landing">
    <template #header>
      <div class="pb-24 flex items-center">
        <div class="flex-1">
          <h1 class="text-3xl md:text-4xl font-light">
            {{ $config.name }}
          </h1>
          <select
            class="form-select"
            @change="switchFundingPot($event.target.value)"
          >
            <option
              v-for="pot in pots"
              :key="pot.id"
              :selected="funding.id === pot.id"
              :value="pot.id"
            >
              {{ pot.name }}
            </option>
          </select>
        </div>
        <div
          v-if="hasGuidancePack"
          class="flex-no-shrink ml-8 w-48"
        >
          <app-button
            tag="a"
            :href="route('competitions.download.' + competitionGuidancePack.file_extension, {
              competition: competitionGuidancePack.uuid,
            })"
            colour="white"
          >
            {{ $config.application_competition_short_name }} Guidance Pack
          </app-button>
        </div>
      </div>
    </template>
    <div class="relative z-20 container -mt-32">
      <div class="md:flex -mx-4 lg:-mx-6">
        <div
          v-if="eoi"
          class="md:w-1/3 flex"
        >
          <div
            :class="['w-full shadow-lg m-4 lg:m-6 flex flex-col relative', {
              'bg-secondary-500': eoi.statuses.open,
              'bg-grey-300': !eoi.statuses.open
            }]"
          >
            <div
              :class="['mt-2 p-6 flex-1', {
                'bg-white': eoi.statuses.open,
                'bg-grey-200 text-grey-600': !eoi.statuses.open
              }]"
            >
              <div>
                <app-badge
                  v-if="userEoi && userEoi.submitted_at"
                  class="absolute pin-r pin-t mt-6 mr-6"
                  :type="userApplication
                    ? 'positive'
                    : userEoi.rejected_at
                      ? 'negative'
                      : 'neutral'"
                  :icon="userApplication
                    ? 'check'
                    : userEoi.rejected_at
                      ? 'close-circle'
                      : 'time'"
                >
                  <template v-if="userApplication">
                    Successful EOI
                  </template>
                  <template v-else-if="userEoi.rejected_at">
                    Unsuccessful
                  </template>
                  <template v-else-if="userEoi.submitted_at">
                    Under Review
                  </template>
                </app-badge>
                <h1
                  :class="['text-6xl leading-none', {
                    'text-primary-500': eoi.statuses.open,
                    'text-grey-300': !eoi.statuses.open
                  }]"
                >
                  <span class="sr-only">Step</span> 1.
                </h1>
                <h2
                  :class="['text-sm uppercase mb-4', {
                    'text-grey-900': eoi.statuses.open,
                    'text-grey-400': !eoi.statuses.open
                  }]"
                >
                  Expression of interest
                </h2>
                <dl>
                  <div class="flex justify-between">
                    <dt class="w-1/3 font-bold">
                      Opens:
                    </dt>
                    <dd>
                      {{ formatDate(eoi.application_start_date) }}
                    </dd>
                  </div>
                  <div class="flex justify-between">
                    <dt class="w-1/3 font-bold">
                      Closes:
                    </dt>
                    <dd>
                      {{ formatDate(eoi.application_end_date) }}
                    </dd>
                  </div>
                </dl>
                <hr
                  class="my-4 border-b border-grey-300"
                />
                <p>
                  Complete your expression of interest for this programme. EOIs
                  will be reviewed by the APC & Zenzic team and those that fall
                  within scope will be invited to submit a full application.
                </p>
              </div>
              <div class="mt-auto pt-8 lg:w-48">
                <app-button
                  v-if="!userEoi"
                  :disabled="!eoi.statuses.open"
                  @click="eoiConfirmationModalVisible = true"
                >
                  Start Process
                </app-button>
                <app-button
                  v-if="userEoi"
                  tag="a"
                  :href="route('applications.view', { application: userEoi.uuid })"
                >
                  <template v-if="userEoi.submitted_at">
                    Submitted EOI
                  </template>
                  <template v-else>
                    Continue EOI
                  </template>
                </app-button>
              </div>
            </div>
          </div>
        </div>
        <div
          :class="['flex', {
            'md:w-1/3': eoi || project,
            'md:w-1/2': !eoi
          }
          ]"
        >
          <div
            :class="['w-full shadow-lg m-4 lg:m-6 flex flex-col relative', {
              'gradient-primary': application.statuses.open && !hasPassed,
              'bg-grey-300': !application.statuses.open || hasPassed
            }]"
            style="--rotation: -45deg; --from: 70%; --to: 100%"
          >
            <div
              :class="['mt-2 p-6 flex-1', {
                'bg-white': application.statuses.open && !hasPassed,
                'bg-grey-200 text-grey-600': !application.statuses.open || hasPassed
              }]"
            >
              <div>
                <app-badge
                  v-if="userApplication && (userApplication.submitted_at || hasExtension)"
                  class="absolute pin-r pin-t mt-6 mr-6"
                  :type="userApplication.submitted_at
                    ? 'positive'
                    : userApplication.rejected_at
                      ? 'negative'
                      : 'neutral'"
                  :icon="userApplication.submitted_at
                    ? 'check'
                    : userApplication.rejected_at
                      ? 'close-circle'
                      : 'time'"
                >
                  <template v-if="userApplication.submitted_at">
                    Submitted
                  </template>
                  <template v-else-if="hasExtension">
                    Extension Granted
                  </template>
                </app-badge>
                <h1
                  :class="['text-6xl leading-none', {
                    'text-primary-500': application.statuses.open && !hasPassed,
                    'text-grey-300': !application.statuses.open || hasPassed
                  }]"
                >
                  <span class="sr-only">Step</span> {{ eoi ? "2" : "1" }}.
                </h1>
                <h2
                  :class="['text-sm uppercase mb-4', {
                    'text-grey-900': application.statuses.open && !hasPassed,
                    'text-grey-400': !application.statuses.open || hasPassed
                  }]"
                >
                  {{ $config.application_competition_short_name }} Application
                </h2>
                <dl v-if="application">
                  <div class="flex justify-between">
                    <dt class="w-1/3 font-bold">
                      Opens:
                    </dt>
                    <dd>
                      {{ formatDate(application.application_start_date) }}
                    </dd>
                  </div>
                  <div class="flex justify-between">
                    <dt class="w-1/3 font-bold">
                      Closes:
                    </dt>
                    <dd>
                      {{ formatDate(application.application_end_date) }}
                    </dd>
                  </div>
                  <div
                    v-if="hasExtension"
                    class="flex justify-between"
                  >
                    <dt class="w-1/3 font-bold">
                      Extension Until:
                    </dt>
                    <dd>
                      {{ applicationExtendedUntil }}
                    </dd>
                  </div>
                </dl>
                <hr
                  v-if="application"
                  class="my-4 border-b border-grey-300"
                />
                <p>
                  Your
                  {{ $config.application_competition_short_name }} application,
                  once submitted, will be reviewed by the Funding Team.
                </p>
              </div>
              <div
                v-if="userApplication"
                class="mt-auto pt-8 lg:w-48"
              >
                <app-button
                  v-if="userApplication.application_progress_percentage > 0"
                  tag="a"
                  :href="route('applications.view', {
                    application: userApplication.uuid
                  })"
                >
                  <template v-if="userApplication.submitted_at">
                    Submitted Application
                  </template>
                  <template v-else-if="!application.statuses.open && !hasExtension">
                    View Application
                  </template>
                  <template v-else>
                    Continue Application
                  </template>
                </app-button>
                <app-button
                  v-else
                  :disabled="!application.statuses.open && !hasExtension"
                  @click="applicationConfirmationModalVisible = true"
                >
                  Start Application
                </app-button>
              </div>
              <div
                v-else-if="!eoi"
                class="mt-auto pt-8 lg:w-48"
              >
                <app-button
                  :disabled="!application.statuses.open && !hasExtension"
                  @click="applicationConfirmationModalVisible = true"
                >
                  Start Application
                </app-button>
              </div>
            </div>
          </div>
        </div>
        <div
          :class="['flex', {
            'md:w-1/3': eoi || project,
            'md:w-1/2': !eoi
          }]"
        >
          <div
            style="--rotation: -45deg; --from: 70%; --to: 100%"
            :class="['w-full shadow-lg m-4 lg:m-6 flex flex-col relative', {
              'gradient-primary': userApplication && feedback.length,
              'bg-grey-300': !userApplication || !feedback.length
            }]"
          >
            <div
              :class="['mt-2 p-6 flex-1', {
                'bg-white': userApplication && feedback.length,
                'bg-grey-200 text-grey-600': !userApplication || !feedback.length
              }]"
            >
              <div>
                <h1
                  :class="['text-6xl leading-none', {
                    'text-primary-500': userApplication && feedback.length,
                    'text-grey-300': !userApplication || !feedback.length
                  }]"
                >
                  <span class="sr-only">Step</span> {{ eoi ? "3" : "2" }}.
                </h1>
                <h2
                  :class="['text-sm uppercase mb-4', {
                    'text-grey-900': userApplication && feedback.length,
                    'text-grey-400': !userApplication || !feedback.length
                  }]"
                >
                  {{ $config.application_competition_short_name }} Feedback
                </h2>
                <dl>
                  <div
                    v-for="phase in assessmentPhases"
                    :key="phase.id"
                    class="flex justify-between"
                  >
                    <dt class="w-2/3 font-bold">
                      {{ phase.name }}
                    </dt>
                    <dd>
                      <icon
                        v-if="getPhaseFeedbackStatus(phase.id) != 'na'"
                        :icon="getPhaseFeedbackStatus(phase.id) === 'approve'
                          ? 'check'
                          : getPhaseFeedbackStatus(phase.id) === 'reject'
                            ? 'close-circle'
                            : 'time'"
                        :class="['w-5 h-5', {
                          'icons-positive': getPhaseFeedbackStatus(phase.id) === 'approve',
                          'icons-negative': getPhaseFeedbackStatus(phase.id) === 'reject',
                          'icons-neutral': getPhaseFeedbackStatus(phase.id) === 'pending'
                        }]"
                      />
                      <span
                        v-else
                        class="mr-1"
                      >
                        &mdash;
                      </span>
                    </dd>
                  </div>
                </dl>
                <hr class="my-4 border-b border-grey-300" />
              </div>
              <div class="mt-auto pt-8 lg:w-48">
                <app-button
                  tag="a"
                  :disabled="!feedback.length || !userApplication"
                  :href="userApplication && feedback.length
                    ? route('applications.feedback.view', {
                      application: userApplication.uuid
                    })
                    : '#'"
                >
                  View Feedback
                </app-button>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="project"
          class="w-1/3"
        >
          <div
            style="--rotation: -45deg; --from: 70%; --to: 100%"
            :class="['w-full shadow-lg m-4 lg:m-6 flex flex-col relative', {
              'gradient-primary': project,
              'bg-grey-300': !project
            }]"
          >
            <div
              :class="['mt-2 p-6 flex-1', {
                'bg-white': project,
                'bg-grey-200 text-grey-600': !project
              }]"
            >
              <div>
                <h1
                  :class="['text-6xl leading-none', {
                    'text-primary-500': project && project,
                    'text-grey-300': !project
                  }]"
                >
                  <span class="sr-only">Step</span> 3.
                </h1>
                <h2
                  :class="['text-sm uppercase mb-4', {
                    'text-grey-900': userApplication && feedback.length,
                    'text-grey-400': !userApplication || !feedback.length
                  }]"
                >
                  {{ project.ref }} Management
                </h2>
                <dl>
                  <div
                    class="flex justify-between"
                  >
                    <dt class="w-1/3 font-bold">
                      Project Duration
                    </dt>
                    <dd>
                      {{ formatDate(project.competition.start_date) }} - {{ formatDate(project.competition.end_date) }}
                    </dd>
                  </div>
                  <div
                    class="flex justify-between"
                  >
                    <dt class="w-1/3 font-bold">
                      Contracted
                    </dt>
                    <dd>
                      <icon
                        :icon="project.contracts.length ? 'check' : 'close-circle'"
                        :class="['w-5 h-5', {
                          'icons-positive': project.contracts.length,
                          'icons-negative': !project.contracts.length
                        }]"
                      />
                    </dd>
                  </div>
                  <template v-if="project.current_monitoring_items.length && !project.terminated_at">
                    <div class="w-full">
                      <dt class="font-bold">
                        Review Period: {{ formatDate(project.current_monitoring_items[0].open_date, 'MMMM yyyy') }}
                      </dt>
                      <dd class="flex -mx-px">
                        <div
                          v-for="status in project.current_monitoring_items"
                          :key="status.id"
                          class="border border-grey-100 h-6 text-center flex items-center justify-center flex-1 rounded"
                          :class="{
                            'bg-positive-600': status.partner_status === 3,
                            'bg-neutral-500': status.partner_status === 2,
                            'bg-negative-600': status.partner_status === 1,
                            'bg-grey-600': !status.partner_status
                          }"
                        >
                          <icon
                            :icon="
                              status.partner_monitoring_completed
                                ? 'check'
                                : 'important'
                            "
                            class="w-4 h-4"
                            :class="{
                              'icons-positive': status.partner_status === 3,
                              'icons-neutral': status.partner_status === 2,
                              'icons-negative': status.partner_status === 1,
                              'icons-grey': !status.partner_status
                            }"
                          />
                        </div>
                      </dd>
                    </div>
                  </template>
                </dl>
                <hr class="my-4 border-b border-grey-300" />
              </div>
              <div class="mt-auto pt-8 lg:w-48">
                <app-button
                  tag="a"
                  :href="route('projects.view', project.uuid)"
                >
                  Manage Project
                </app-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <portal to="modals">
      <app-modal
        v-if="eoiConfirmationModalVisible"
        class="text-white"
        @close="eoiConfirmationModalVisible = false"
      >
        <div class="max-w-md mx-auto content">
          <icon
            icon="important"
            class="icons-primary w-16 h-16 mb-2"
          />
          <h2 class="mb-1">
            Expression of Interest Confirmation
          </h2>
          <p>
            All your application information disclosed deliberately or otherwise
            will be treated by APC & Zenzic as confidential information and
            commercially sensitive as exempted under the Freedom of Information
            Act 2000 or any related legislation, unless otherwise marked or
            agreed in writing. APC & Zenzic will share the submitted
            applications with appointed independent external assessors, who are
            themselves bound by confidentiality terms.
          </p>
          <p>
            For the avoidance of doubt, it is at your discretion and must be
            with the agreement of the relevant third parties, if you choose to
            disclose confidential information of third parties to APC & Zenzic,
            at which point it will be treated as confidential information by APC
            & Zenzic unless marked otherwise.
          </p>
          <p v-if="eoi && eoi.guidance_pack_file_id">
            You are also agreeing that you have read and understood the
            <a
              :href="route('competitions.download', { competition: eoi.uuid })"
              target="_blank"
            >{{ $config.application_competition_short_name }} Overview Document</a>.
          </p>
          <p>
            The deadline for applications is
            <strong>{{ formatDate(eoi.application_end_date) }}</strong>.
          </p>
          <p class="mt-12">
            <app-button
              colour="confirm"
              @click="apply(eoi)"
            >
              I agree
            </app-button>
            <app-button
              colour="close"
              @click="eoiConfirmationModalVisible = false"
            >
              I disagree
            </app-button>
          </p>
        </div>
      </app-modal>
    </portal>
    <portal to="modals">
      <app-modal
        v-if="applicationConfirmationModalVisible"
        class="text-white"
        @close="applicationConfirmationModalVisible = false"
      >
        <div class="max-w-md mx-auto content">
          <icon
            icon="important"
            class="icons-primary w-16 h-16 mb-2"
          />
          <h2 class="mb-1">
            Application Confirmation
          </h2>
          <p class="font-bold">
            The deadline for applications is
            <strong>{{ formatDate(application.application_end_date) }}</strong>.
          </p>
          <div
            v-if="terms"
            v-html="parseMarkdown(terms)"
          />
          <div class="mt-4 pt-6 border-t border-grey-700">
            <h2 class="mb-4">
              Before proceeding, please confirm the following statements are
              true:
            </h2>
            <label class="flex mb-2">
              <input
                v-model="preValidation.uk_registered"
                type="checkbox"
                :value="true"
              />
              <span class="ml-6">I am a UK registered SME.</span>
            </label>
            <label class="flex mb-2">
              <input
                v-model="preValidation.trl_check"
                type="checkbox"
                :value="true"
              />
              <span class="ml-6">The opening TRL for this project is between 2-5.</span>
            </label>
            <label class="flex mb-2">
              <input
                v-model="preValidation.ip"
                type="checkbox"
                :value="true"
              />
              <span class="ml-6">I own the IP that I intend to exploit with this programme,
                and have an existing license agreement in place.</span>
            </label>
            <div class="mb-4">
              <p>
                I will have the correct permits and licences in place to carry
                out my Mobilise Accelerator technology development.
              </p>
              <div
                class="ml-6"
                style="display: grid; grid-template-columns: 1fr 1fr; gap: .5rem;"
              >
                <label class="flex mb-2">
                  <input
                    v-model="preValidation.required_licenses"
                    type="radio"
                    :value="2"
                  />
                  <span class="ml-6">Yes</span>
                </label>
                <label class="flex mb-2">
                  <input
                    v-model="preValidation.required_licenses"
                    type="radio"
                    :value="1"
                  />
                  <span class="ml-6">Yes, in the process of being applied for</span>
                </label>
                <label class="flex mb-2">
                  <input
                    v-model="preValidation.required_licenses"
                    type="radio"
                    :value="0"
                  />
                  <span class="ml-6">No</span>
                </label>
                <label class="flex mb-2">
                  <input
                    v-model="preValidation.required_licenses"
                    type="radio"
                    :value="-1"
                  />
                  <span class="ml-6">N/A</span>
                </label>
              </div>
              <ProjectNoticeItem
                v-if="preValidation.required_licenses === 0"
                :notice="{'type': 'negative', 'content' : 'Please contact the APC Buisness Development Team for further guidance.'}"
              />
            </div>
            <label class="flex mb-2">
              <input
                v-model="preValidation.animal_testing"
                type="checkbox"
                :value="true"
              />
              <span class="ml-6">My project does not involve any trials with animals or requires
                animal testing.</span>
            </label>
            <label class="flex mb-2">
              <input
                v-model="preValidation.terms_conditions"
                type="checkbox"
                :value="true"
              />
              <span class="ml-6">I agree to all the terms & conditions</span>
            </label>
            <p class="italic mt-2">
              If you are unable to confirm any of the above statements, please
              contact the APC Business Development Team for further guidance and
              support.
            </p>
          </div>
          <p class="mt-12 flex md:flex-row flex-col">
            <app-button
              colour="confirm"
              :disabled="!preValidationPassed"
              @click="apply(application)"
            >
              {{ preValidationPassed ? "Start application" : "Please confirm the above" }}
            </app-button>
            <span class="w-3 h-3"></span>
            <app-button
              colour="close"
              @click="applicationConfirmationModalVisible = false"
            >
              Cancel
            </app-button>
          </p>
        </div>
      </app-modal>
    </portal>
  </app-layout>
</template>

<script>
import AppButton from "../components/app/AppButton.vue";
import parseISO from "date-fns/parseISO";
import isBefore from "date-fns/isBefore";
import subDays from "date-fns/subDays";
import { get } from "lodash";
import Turbolinks from "turbolinks";
import ProjectNoticeItem from "../projects/components/ProjectNoticeItem.vue";
export default {
  name: "UserDashboard",
  components: {
    AppButton,
    ProjectNoticeItem
  },
  data() {
    return {
      eoiConfirmationModalVisible: false,
      applicationConfirmationModalVisible: false,
      preValidation: {
        uk_registered: false,
        trl_check: false,
        ip: false,
        required_licenses: null, // 2: yes | 1: yes, in progress | 0: no | -1: N/A
        animal_testing: false,
        terms_conditions: false
      }
    };
  },
  computed: {
    hasGuidancePack() {
      return (
        (this.eoi && this.eoi.guidance_pack_file_id) ||
        (this.application && this.application.guidance_pack_file_id)
      );
    },
    competitionGuidancePack() {
      if (this.eoi && this.eoi.guidance_pack_file_id) {
        return {
          uuid: this.eoi.uuid,
          file_extension: this.eoi.guidance_pack_file.extension
        };
      }
      if (this.application && this.application.guidance_pack_file_id) {
        return {
          uuid: this.application.uuid,
          file_extension: this.application.guidance_pack_file.extension
        };
      }
      return null;
    },
    terms() {
      return this.state.terms || "";
    },
    preValidationPassed() {
      return Object.values(this.preValidation).every(v => {
        // this is for the options group
        if (typeof v == 'number') {
          return v != 0
        }
        return v
      });
    },
    competitions() {
      return this.state.competitions ?? [];
    },
    private_competitions() {
      // as we're not running an EOI, just return all competitions
      if (!this.eoi) return this.competitions;
      return this.state.private_competitions ?? [];
    },
    applications() {
      return this.state.applications ?? [];
    },
    project() {
      return this.state.project;
    },
    funding() {
      return this.state.funding;
    },
    eoi() {
      return this.competitions.find(
        competition => competition?.type?.key === "EOI"
      );
    },
    pots() {
      return this.state.pots;
    },
    userEoi() {
      if (!this.eoi) return;
      return this.applications.find(
        application => application?.competition?.uuid === this.eoi.uuid
      );
    },
    application() {
      return this.private_competitions.find(
        competition => competition?.type?.key === "MOB"
      ) ?? {
        statuses: {
          open: false
        }
      };
    },
    userApplication() {
      if (!this.application) return;
      return this.applications.find(
        application => application?.competition?.uuid === this.application.uuid
      );
    },
    applicationExtendedUntil() {
      if (this.userApplication) {
        return "--";
      }

      /* eslint-disable */
      return this.userApplication?.application_extended_until
        ? format(
          parseISO(this.userApplication?.application_extended_until),
          "do MMMM yyyy HH:mm"
        )
        : "--";
      /* eslint-enable */
    },

    feedback() {
      return this.state.feedback ?? [];
    },

    feedbackContainsReject() {
      return this.feedback.some(item => item.status === "reject");
    },

    assessmentPhases() {
      return this.state.assessment_phases ?? [];
    },

    userProject() {
      return false;
    },
    hasPassed() {
      if (this.userApplication?.application_extended_until !== null) {
        return isBefore(
          parseISO(this.userApplication?.application_extended_until),
          new Date()
        );
      }
      return isBefore(this.application.application_end_date, new Date());
    },
    hasExtension() {
      if (!this.userApplication) {
        return false;
      }

      return !!this.userApplication?.application_extended_until;
    }
  },
  created() {
    document.title = `Dashboard :: ${document.title}`;
  },
  methods: {
    apply(competition) {
      const { uuid } = competition;
      axios
        .post(route("competitions.apply", { competition: uuid }), {
          pre_validation: this.preValidation
        })
        .then(({ data }) => this.success(data))
        .catch(() =>
          alert(
            "Sorry, there appears to have been an error creating your application. Please try again later"
          )
        );
    },
    getPhaseFeedbackStatus(phaseId) {
      const feedback = this.feedback.find(
        item => get(item, "marking_phase.id") === phaseId
      );

      if (!feedback) {
        return "na";
      }

      return feedback.confirmed_status;
    },
    subDays,
    success({ project_id }) {
      Turbolinks.visit(route("applications.view", { id: project_id }));
    },
    switchFundingPot(potId) {
      Turbolinks.visit(route("home", { programme: potId }));
    },
    formatDate(date, fmt = 'do MMMM yyyy', defaultValue = "--") {
      return date ? this.format(parseISO(date), fmt) : defaultValue;
    },
  }
};
</script>
